import mqtt, { type IClientOptions, type ISubscriptionGrant, type MqttClient } from 'mqtt';
import { generateUUID } from '@/lib/ids';

/**
 * The MQTT engine — port of `src/store/plugins/mqttConnection.ts` (docs/MIGRATION.md §7).
 *
 * Owns one mqtt.js client: connect, subscribe, heartbeat, message dispatch, teardown. It
 * knows nothing about React and nothing about the store — `MqttProvider` passes the four
 * store touchpoints in as callbacks, which is what makes every branch below testable without
 * a broker.
 *
 * ## The import path
 *
 * `import mqtt from 'mqtt'` resolves through the package's `exports` map. Under Vite the
 * `browser` condition wins and it lands on `dist/mqtt.esm.js`, the WebSocket build — which
 * has **only a default export**, so `import { connect } from 'mqtt'` compiles (the types
 * describe the Node build) and then fails at runtime in the bundle. Everything here goes
 * through the default export for that reason. Type-only imports are erased and are safe.
 *
 * ## Logging
 *
 * Verbose and prefixed `[mqtt]`, with the exact topic strings. A refused subscribe is
 * invisible in the UI — the widget simply never receives data — so these lines are the only
 * way anyone finds out that a display's ACL does not match what it asked for.
 */

/** Matches `reconnectPeriod`; only used when `mqtt.connect` itself throws. */
const CONNECT_RETRY_MS = 5000;

/** MQTT 3.1.1 SUBACK failure code. The broker accepted the packet and refused the topic. */
const SUBSCRIPTION_REFUSED_QOS = 128;

export type MessageContext = {
  baseTopic: string;
  /** `topicItems.get(topic)` — read live, not snapshotted, so late registrations still route. */
  resolveComponentId: (topic: string) => string | undefined;
  deliver: (componentId: string, payload: unknown) => void;
  reload: () => void;
};

export type MqttEngineOptions = MessageContext & {
  url: string;
  /** The preventer token, `<username>-<password>`. Split here, never logged whole. */
  token: string;
  floorCode: string;
  heartBeatMs: number;
  /** Absolute topic keys, exactly as `topicItems` holds them and `savemeta` was told. */
  topics: string[];
  onConnectedChange: (connected: boolean) => void;
};

export type MqttEngineHandle = {
  /** Full teardown: force-close the client, clear the heartbeat, cancel any pending retry. */
  stop: () => void;
};

/**
 * Splits the preventer token into broker credentials.
 *
 * ```ts
 * const username = token.split('-', 1)[0];
 * const password = token.replace(username + '-', '');
 * ```
 *
 * Both halves of that are load-bearing. `split('-', 1)` returns *at most one* element, so
 * `[0]` is everything before the first `-`. `replace` with a string pattern removes only the
 * **first** occurrence of `username-`, which is what lets a password contain `-` and survive
 * intact — `split('-')[1]` would truncate it at the next hyphen and produce a silent
 * authentication failure on a wall-mounted screen. Unit-tested with such a password.
 *
 * A token with no `-` at all yields `username === token` and, since `token-` does not occur
 * in `token`, `password === token`. That is the legacy outcome and it is preserved: the
 * broker will reject it, which is the correct visible failure.
 */
export function splitCredentials(token: string): { username: string; password: string } {
  const username = token.split('-', 1)[0] ?? '';
  const password = token.replace(username + '-', '');
  return { username, password };
}

/**
 * Decodes an MQTT payload to text.
 *
 * Legacy called `message.toString('utf8')`. mqtt.js hands the browser build a `Buffer` shim,
 * where that works; a plain `Uint8Array` (which is what a test, or any other producer,
 * supplies) would stringify to `"123,45,..."` instead. `TextDecoder` produces the identical
 * result for both, including the same U+FFFD substitution on malformed input.
 */
const utf8 = new TextDecoder();

/**
 * Routes one incoming message. Exported for tests — every branch here is a documented
 * failure mode of the legacy client.
 */
export function handleMqttMessage(topic: string, message: Uint8Array, ctx: MessageContext): void {
  const exactRefresh = topic === `${ctx.baseTopic}/refresh`;
  // Legacy: `if (topic.indexOf('refresh') !== -1) window.location.reload()`. A substring
  // match against the whole topic, so *any* topic containing the word reloads the page.
  // Preserved (docs/MIGRATION.md decision #9): remote reload is the only recovery path for a
  // wedged display and tightening this risks losing it. The warning makes the drift visible
  // before someone tightens it on a hunch.
  const looseRefresh = topic.includes('refresh');

  if (looseRefresh && !exactRefresh) {
    console.warn(
      '[mqtt] loose refresh match: "%s" is not "%s/refresh", but the legacy substring rule ' +
        'reloads on it anyway',
      topic,
      ctx.baseTopic,
    );
  }
  if (looseRefresh) {
    console.info('[mqtt] refresh received on "%s" — reloading the display', topic);
    ctx.reload();
    return;
  }

  let payload: unknown;
  try {
    // Unguarded in the legacy client, which is fix #5: one malformed retained message throws
    // inside mqtt.js's message handler and kills the client for the lifetime of the page —
    // every widget then freezes at its last value with nothing on screen to say why.
    payload = JSON.parse(utf8.decode(message));
  } catch (error) {
    console.error('[mqtt] unparseable payload on "%s"; message dropped', topic, error);
    return;
  }

  const componentId = ctx.resolveComponentId(topic);
  if (componentId === undefined) {
    // Either the broker sent something nobody asked for, or a topic string drifted between
    // `registerTopic` and the subscribe. The second is the failure this whole ticket exists
    // to make visible.
    console.warn('[mqtt] message for unregistered topic "%s"; dropped', topic);
    return;
  }

  ctx.deliver(componentId, payload);
}

/**
 * Connects to the broker and keeps the connection alive for the lifetime of the page.
 *
 * Everything that must survive a reconnect lives in the `connect` handler, which mqtt.js
 * re-fires on every automatic reconnect. The legacy app subscribed from a once-only Vuex
 * mutation handler instead, so a display that lost its network for ten seconds came back
 * connected and subscribed to nothing — a frozen screen that looks exactly like a working
 * one (docs/MIGRATION.md §7, fix #4).
 */
export function connectSignageMqtt(options: MqttEngineOptions): MqttEngineHandle {
  const {
    url,
    token,
    baseTopic,
    floorCode,
    heartBeatMs,
    topics,
    resolveComponentId,
    deliver,
    reload,
    onConnectedChange,
  } = options;

  const { username, password } = splitCredentials(token);
  const refreshTopic = `${baseTopic}/refresh`;
  const heartbeatTopic = `${baseTopic}/${floorCode}/heartbeat`;
  const messageContext: MessageContext = { baseTopic, resolveComponentId, deliver, reload };

  let client: MqttClient | undefined;
  let heartbeatTimer: ReturnType<typeof setInterval> | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const clearHeartbeat = () => {
    if (heartbeatTimer !== undefined) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = undefined;
    }
  };

  const publishHeartbeat = () => {
    // `userName`, capital N, and the value is the *username half of the token* — not the
    // whole token. Both are what the backend reads. `retain: true` so a monitoring consumer
    // sees the last beat of a display that has since gone dark.
    client?.publish(
      heartbeatTopic,
      JSON.stringify({ timestamp: Date.now(), userName: username }),
      { qos: 1, retain: true },
      (error) => {
        if (error) console.error('[mqtt] heartbeat publish failed on "%s"', heartbeatTopic, error);
      },
    );
  };

  const subscribe = (topic: string) => {
    client?.subscribe(topic, (error: Error | null, granted?: ISubscriptionGrant[]) => {
      if (error) {
        console.error(
          '[mqtt] SUBSCRIBE FAILED "%s" — this widget will never receive data',
          topic,
          error,
        );
        return;
      }
      const refused = granted?.some((g) => g.qos === SUBSCRIPTION_REFUSED_QOS) ?? false;
      if (refused) {
        console.error(
          '[mqtt] SUBSCRIBE REFUSED "%s" (SUBACK 0x80) — the broker ACL does not cover this ' +
            'topic. Compare it against the savemeta list logged above.',
          topic,
        );
        return;
      }
      console.info('[mqtt] subscribed "%s" (qos %s)', topic, String(granted?.[0]?.qos ?? '?'));
    });
  };

  const onConnect = () => {
    console.info('[mqtt] connected to %s as "%s"', url, username);
    onConnectedChange(true);

    // Re-run on every reconnect, which is the point.
    for (const topic of topics) subscribe(topic);
    subscribe(refreshTopic);

    // A reconnect must not stack intervals — the legacy code assigned its handle to a
    // shadowed `const timer`, leaving the outer `let timer` undefined and the interval
    // impossible to clear (fix #2).
    clearHeartbeat();
    console.info('[mqtt] heartbeat -> "%s" every %dms', heartbeatTopic, heartBeatMs);
    heartbeatTimer = setInterval(publishHeartbeat, heartBeatMs);
  };

  const start = () => {
    if (stopped) return;

    const connectOptions: IClientOptions = {
      clientId: generateUUID(),
      username,
      password,
      keepalive: 60,
      reconnectPeriod: 5000,
    };

    try {
      console.info(
        '[mqtt] connecting to %s (clientId %s, %d registered topics + refresh)',
        url,
        connectOptions.clientId,
        topics.length,
      );
      client = mqtt.connect(url, connectOptions);
    } catch (error) {
      // The legacy code caught here and then dereferenced the undefined client on the next
      // line, so the catch bought nothing. A malformed `mqttUrl` in a config is the realistic
      // cause, and it is not something that fixes itself — but retrying costs nothing and an
      // operator may well correct the config on the server while the display is up.
      console.error(
        '[mqtt] connect() threw for %s; retrying in %dms',
        url,
        CONNECT_RETRY_MS,
        error,
      );
      retryTimer = setTimeout(start, CONNECT_RETRY_MS);
      return;
    }

    client.on('connect', onConnect);
    client.on('message', (topic, message) => {
      handleMqttMessage(topic, message, messageContext);
    });
    // Legacy had this one with a `// never fires` comment and nothing else. It does fire —
    // on a bad URL, a TLS failure, or a rejected credential pair, which is precisely the
    // symptom a display shows when the preventer token has gone stale.
    client.on('error', (error) => {
      console.error('[mqtt] client error', error);
    });
    client.on('close', () => {
      console.warn('[mqtt] connection closed');
      onConnectedChange(false);
    });
    client.on('offline', () => {
      console.warn('[mqtt] client offline');
      onConnectedChange(false);
    });
    client.on('reconnect', () => {
      // mqtt.js owns the reconnect loop via `reconnectPeriod`. Nothing here schedules a
      // second one on top of it.
      console.info('[mqtt] reconnecting to %s', url);
    });
  };

  start();

  return {
    stop: () => {
      stopped = true;
      if (retryTimer !== undefined) {
        clearTimeout(retryTimer);
        retryTimer = undefined;
      }
      clearHeartbeat();
      if (client) {
        // Listeners first: `end(true)` skips the graceful DISCONNECT but still emits `close`,
        // and a handler firing after teardown would write to a store this provider no longer
        // owns.
        client.removeAllListeners();
        client.end(true);
        client = undefined;
      }
      onConnectedChange(false);
      console.info('[mqtt] client torn down');
    },
  };
}
